import { Sidebar } from "@xola/ui-kit";
import React, { memo, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { Plugin } from "types";
import { useUser } from "../../hooks/useAccount";
import { useInstalledPlugins } from "../../hooks/usePlugins";
import { hasAnyRole, isAffiliate } from "../../models/User";

export const NPS_SUBMENUS = [
    { label: "Reviews", path: "reviews" },
    { label: "Overview", path: "overview" },
    { label: "Segments", path: "segments" },
    { label: "Map", path: "map" },
] as const;

interface PluginMenuItemProps {
    plugin: Plugin;
}

const PluginMenuItem = memo(({ plugin }: PluginMenuItemProps) => {
    const history = useHistory();
    const { ui } = plugin;

    const handleClick = useCallback(
        (path?: string) => {
            const url = path ? `/pluginCanvas/${plugin.id}/${path}` : `/pluginCanvas/${plugin.id}`;
            history.push(url);
        },
        [plugin.id, history],
    );

    if (ui?.menu === "reports.Disputes") {
        return null; // Exclude disputes plugin from menu as it's handled separately
    }

    // Special case for Net Promoter Score
    if (ui?.menu === "reports.NetPromoterScore") {
        return (
            <div key={plugin.id}>
                <Sidebar.Link isSubMenuItem onClick={() => handleClick(NPS_SUBMENUS[0].path)}>
                    {ui?.label ?? plugin.id}
                </Sidebar.Link>
            </div>
        );
    }

    // Default rendering for other plugins
    return (
        <Sidebar.Link key={plugin.id} isSubMenuItem onClick={() => handleClick()}>
            {ui?.label ?? plugin.id}
        </Sidebar.Link>
    );
});

interface CustomMenuItemsProps {
    route: string;
}

export const CustomMenuItems = memo(({ route }: CustomMenuItemsProps) => {
    const user = useUser();
    const { data: plugins = [] } = useInstalledPlugins();

    // Filter plugins for this route and by user permissions
    const filteredPlugins = useMemo(() => {
        return plugins
            .filter((plugin) => plugin.ui?.menu?.startsWith(route))
            .filter((plugin) => {
                if (isAffiliate(user)) return false;
                return plugin.permissions.length === 0 || hasAnyRole(user, plugin.permissions);
            });
    }, [plugins, route, user]);

    if (filteredPlugins.length === 0) {
        return null;
    }

    return (
        <>
            {filteredPlugins.map((plugin) => (
                <PluginMenuItem key={plugin.id} plugin={plugin} />
            ))}
        </>
    );
});
